import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";

/**
 * Single source of truth for the JWT token pair + the cached "me" payload.
 * Persisted to localStorage so a refresh keeps the user signed in;
 * sensitive enough that we DO NOT persist anything beyond the access token,
 * refresh token and the public user object — never the password, never the
 * full org tree.
 */

export type Role =
  | "super_admin"
  | "org_admin"
  | "safety_manager"
  | "operator"
  | "worker";

/** A site the user is scoped to (R1 `me` payload). */
export interface MeSite {
  id: number;
  name: string;
  code: string;
}

export interface MeUser {
  id: number;
  username: string;
  email: string;
  role: Role;
  organization?: { id: number; name: string; slug: string } | null;
  /** 연락처 — SMS 경보 수신처. 본인이 프로필에서 바꾼다. */
  phone?: string;
  locale: string;
  /**
   * 개인 음성경보 주 언어(`ko`·`ja`…). 백엔드가 아직 노출하지 않을 수 있어
   * optional 이며, 없으면 브라우저에 저장된 설정을 그대로 쓴다.
   */
  voice_lang?: string | null;
  is_active: boolean;
  last_login: string | null;
  /** 마지막 비밀번호 변경 시각. 한 번도 바꾼 적 없으면 null. */
  last_password_change?: string | null;
  date_joined: string;
  /** R1: sites this user is assigned to. Empty for super/org_admin (unscoped). */
  sites?: MeSite[];
  /** R1: "all" (super/org_admin, whole org/platform) or "assigned" (site-scoped). */
  site_scope?: "all" | "assigned";
  /** R2: forces the change-password screen until the user rotates their password. */
  must_change_password?: boolean;
}

interface AuthState {
  access: string | null;
  refresh: string | null;
  user: MeUser | null;
  setSession: (input: { access: string; refresh: string; user: MeUser }) => void;
  setUser: (user: MeUser) => void;
  clear: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    subscribeWithSelector((set) => ({
      access: null,
      refresh: null,
      user: null,
      setSession: ({ access, refresh, user }) => set({ access, refresh, user }),
      setUser: (user) => set({ user }),
      clear: () => set({ access: null, refresh: null, user: null }),
    })),
    {
      name: "expertgas.auth.v2",
      version: 1,
      partialize: (s) => ({ access: s.access, refresh: s.refresh, user: s.user }),
    },
  ),
);

/** Selector helper — micro-optimised so unrelated changes don't re-render. */
export const selectAccess = (s: AuthState) => s.access;
export const selectUser   = (s: AuthState) => s.user;
export const selectIsAuth = (s: AuthState) => !!s.access && !!s.user;
